import { useBreite, useTip } from "@/components/Tooltip"
import { Karte, Leer } from "@/components/ui"
import { gruppiere, kwKurz, stat, type AuswertungsDaten, type Filter } from "@/lib/auswertung"
import { farbe, mmss, niceTicks, nf0, nf1 } from "@/lib/format"

interface Props {
  d: AuswertungsDaten
  tp: Float64Array
  ids: number[] // ohne NC-Typ-Filter
  f: Filter
  setTyp: (t: number) => void
}

/** Durchsatz je NC-Typ (ab 5 Proben); Klick setzt den NC-Typ-Filter, die Auswahl wird hervorgehoben. */
export function TypenBalken({ d, tp, ids, f, setTyp }: Props) {
  const [ref, breite] = useBreite<HTMLDivElement>()
  const tip = useTip()
  const gruppen = [...gruppiere(ids, (i) => d.rows.t[i])].map(([t, g]) => ({ t, ...stat(d, tp, g, f.kw) }))
  const zeilen = gruppen.filter((g) => g.n >= 5).sort((a, b) => (b.tp ?? 0) - (a.tp ?? 0))
  const klein = gruppen.length - zeilen.length

  const W = Math.max(300, breite), rowH = 26, top = 22
  const H = top + zeilen.length * rowH + 4
  const lw = 72, rw = 92
  const max = Math.max(1, ...zeilen.map((z) => z.tp ?? 0))
  const ticks = niceTicks(0, max, W < 420 ? 3 : 5)
  const xmax = ticks[ticks.length - 1]
  const x = (v: number) => lw + (v / xmax) * (W - lw - rw)
  const bh = 14, r = 4

  return (
    <Karte
      titel="Durchsatz je NC-Typ"
      unter="Sortiert nach Durchsatz, ab 5 Proben. Klick auf einen Balken setzt den NC-Typ-Filter; die übrigen Filter gelten."
    >
      <div ref={ref} className="w-full min-w-0">
        {!ids.length ? <Leer /> : !zeilen.length ? <Leer>Kein NC-Typ mit mindestens 5 Proben.</Leer> : breite > 0 && (
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Durchsatz je NC-Typ in kg/h" className="block overflow-visible">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={x(t)} x2={x(t)} y1={top - 6} y2={H - 4} stroke={farbe("grid")} strokeWidth={1} />
                <text x={x(t)} y={top - 10} textAnchor="middle" className="fill-muted text-[11px] tabular">{nf0.format(t)}</text>
              </g>
            ))}
            <line x1={x(0)} x2={x(0)} y1={top - 6} y2={H - 4} stroke={farbe("axis")} strokeWidth={1} />
            {zeilen.map((z, j) => {
              const yc = top + j * rowH + rowH / 2
              const gewaehlt = f.typ === z.t
              const w = Math.max(r + 1, x(z.tp ?? 0) - x(0))
              const y0 = yc - bh / 2
              const tipp = (cx: number, cy: number) => tip.zeige(cx, cy, d.dims.typ[z.t], [
                { label: `Durchsatz (${kwKurz(f.kw)})`, farbe: farbe("accent"), wert: `${nf0.format(z.tp ?? 0)} kg/h` },
                { label: "Zykluszeit", wert: `${mmss(z.z ?? 0)} min` },
                { label: "Auswurfgewicht", wert: `${nf1.format(z.aw ?? 0)} kg` },
                { label: "Füllgewicht", wert: z.f == null ? "–" : `${nf1.format(z.f)} kg` },
                { label: "Proben", wert: nf0.format(z.n) },
              ])
              const setze = () => { tip.verstecke(); setTyp(gewaehlt ? -1 : z.t) }
              return (
                <g key={z.t}>
                  <text x={lw - 10} y={yc + 4} textAnchor="end" className="fill-ink2 font-mono text-xs">{d.dims.typ[z.t]}</text>
                  <path
                    fill={f.typ >= 0 && !gewaehlt ? farbe("deemph") : farbe("accent")}
                    d={`M${x(0)},${y0}h${w - r}a${r},${r} 0 0 1 ${r},${r}v${bh - 2 * r}a${r},${r} 0 0 1 -${r},${r}h-${w - r}z`}
                  />
                  <text x={x(0) + w + 6} y={yc + 4} className="fill-ink text-xs font-medium tabular">
                    {nf0.format(z.tp ?? 0)}
                    <tspan dx={6} className="fill-muted text-[11px] font-normal">{`n ${nf0.format(z.n)}`}</tspan>
                  </text>
                  <rect
                    x={0} y={yc - rowH / 2} width={W} height={rowH} fill="transparent" className="cursor-pointer"
                    tabIndex={0} role="button"
                    aria-label={`${d.dims.typ[z.t]}: ${nf0.format(z.tp ?? 0)} kg/h, ${z.n} Proben. Als Filter setzen.`}
                    onPointerMove={(e) => tipp(e.clientX, e.clientY)}
                    onPointerLeave={tip.verstecke}
                    onFocus={(e) => { const b = e.currentTarget.getBoundingClientRect(); tipp(b.left + x(z.tp ?? 0), b.top) }}
                    onBlur={tip.verstecke}
                    onClick={setze}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setze() } }}
                  />
                </g>
              )
            })}
          </svg>
        )}
      </div>
      {klein > 0 && (
        <p className="text-xs text-muted">
          {`${klein} NC-Typ${klein > 1 ? "en" : ""} mit weniger als 5 Proben nicht gezeigt (in der Detailtabelle enthalten).`}
        </p>
      )}
    </Karte>
  )
}
